import { useEffect, useState } from "react";
import { api, formatRupiah, formatDate, errMsg, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, X, Download } from "lucide-react";

export default function Finance() {
  const { user } = useAuth();
  const [tab, setTab] = useState("expenses");
  const [expenses, setExpenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [err, setErr] = useState("");

  async function load() {
    const [e, p, w] = await Promise.all([
      api.get("/expenses"), api.get("/payments"), api.get("/worker-payments")
    ]);
    setExpenses(e.data); setPayments(p.data); setWorkers(w.data);
  }
  useEffect(() => { load(); }, []);

  async function approve(id, ok) {
    try {
      const { data } = await api.post(`/expenses/${id}/approve`, { approve: ok });
      if (data.warning) alert(data.warning);
      await load();
    } catch (ex) { setErr(errMsg(ex)); }
  }

  async function markPaid(w) {
    await api.patch(`/worker-payments/${w.id}`, { status: "Dibayar", payment_date: new Date().toISOString() });
    await load();
  }

  function downloadCsv(entity) {
    downloadFile(`/export/${entity}`, `${entity}.csv`).catch(ex => setErr(errMsg(ex)));
  }

  return (
    <div className="p-4 sm:p-8 space-y-5">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif-display font-bold">Keuangan</h1>
          <p className="text-sm text-[#536263]">Approval pengeluaran, pembayaran, dan tim lapangan</p>
          <div className="mt-2 h-px w-12 bg-[#B87333]"></div>
        </div>
      </header>

      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {[["expenses","Pengeluaran"],["payments","Pembayaran Klien"],["workers","Upah Tim"]].map(([k,l]) =>
          <button key={k} onClick={() => setTab(k)} data-testid={`finance-tab-${k}`}
            className={`chip whitespace-nowrap ${tab === k ? "bg-[#0F5257] text-white" : "bg-[#F2EEE7] text-[#536263]"}`}>{l}</button>
        )}
      </div>

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}

      {tab === "expenses" && (
        <div className="space-y-3" data-testid="expenses-list">
          <div className="flex justify-between items-center">
            <h2 className="font-serif-display text-lg font-semibold">Pengajuan Pengeluaran</h2>
            <button onClick={() => downloadCsv("expenses")} className="btn-copper"><Download size={16} />Export CSV</button>
          </div>
          {expenses.map(e => (
            <div key={e.id} className="card-editorial p-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`chip ${e.status === "Disetujui" ? "bg-emerald-100 text-emerald-800" : e.status === "Ditolak" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-900"}`}>{e.status}</span>
                    <span className="text-xs text-[#8A999A]">{e.category}</span>
                  </div>
                  <div className="font-medium mt-1">{e.description}</div>
                  <div className="text-xs text-[#8A999A]">Diajukan: {e.submitted_by} · {formatDate(e.created_at)}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-num text-lg font-bold text-[#0F5257]">{formatRupiah(e.amount)}</div>
                  {e.status === "Menunggu" && user?.role === "Owner" && (
                    <div className="flex gap-1 mt-2">
                      <button onClick={() => approve(e.id, true)} className="chip bg-emerald-100 text-emerald-800" data-testid={`approve-${e.id}`}><Check size={14} />Setujui</button>
                      <button onClick={() => approve(e.id, false)} className="chip bg-red-100 text-red-800" data-testid={`reject-${e.id}`}><X size={14} />Tolak</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {expenses.length === 0 && <div className="text-center p-12 text-[#8A999A]">Belum ada pengajuan</div>}
        </div>
      )}

      {tab === "payments" && (
        <div className="space-y-2" data-testid="payments-list">
          <div className="flex justify-between items-center">
            <h2 className="font-serif-display text-lg font-semibold">Pembayaran Klien</h2>
            <button onClick={() => downloadCsv("payments")} className="btn-copper"><Download size={16} />Export CSV</button>
          </div>
          {payments.map(p => (
            <div key={p.id} className="card-editorial p-3 flex justify-between items-center">
              <div><div className="text-sm font-medium">{p.note || "Pembayaran"}</div><div className="text-xs text-[#8A999A]">{formatDate(p.date)} · {p.method}</div></div>
              <div className="font-mono-num font-bold text-[#0F5257]">{formatRupiah(p.amount)}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "workers" && (
        <div className="space-y-2" data-testid="workers-list">
          <h2 className="font-serif-display text-lg font-semibold">Pembayaran Tim/Freelancer</h2>
          {workers.map(w => (
            <div key={w.id} className="card-editorial p-4 flex items-center justify-between">
              <div><div className="font-medium">{w.worker_name}</div><div className="text-xs text-[#8A999A]">{w.role}</div></div>
              <div className="text-right">
                <div className="font-mono-num font-semibold">{formatRupiah(w.amount)}</div>
                {w.status === "Belum" ? (
                  <button onClick={() => markPaid(w)} className="chip bg-emerald-100 text-emerald-800 mt-1" data-testid={`pay-worker-${w.id}`}>Tandai Dibayar</button>
                ) : <span className="chip bg-emerald-100 text-emerald-800 mt-1">Dibayar</span>}
              </div>
            </div>
          ))}
          {workers.length === 0 && <div className="text-center p-12 text-[#8A999A]">Belum ada data</div>}
        </div>
      )}
    </div>
  );
}
